import React from 'react'
import SeoPage from '../components/SeoPage'

import Img from '../components/Image'
import { graphql, Link } from 'gatsby'

const ContentPage = ({ data, location, ...props }) => {
    return (
        <SeoPage
            leadImage={data.img0.childImageSharp.fluid.src}
            location={location}
            title="Accusation de garde et contrôle du véhicule et alcool au volant au Québec!"
            description="Nous vous expliquons en quoi la garde ou le contrôle du véhicule peuvent entraîner une accusation de conduite avec facultés affaiblies par l’alcool, ainsi que les moyens de s’en défendre."
            fr={undefined}
            lawType="criminal"
            specialtyText="pour Alcool au volant">
            <p>
                <strong>
                    Au Québec, il est possible d’être accusé de conduite avec
                    les facultés affaiblies par l’alcool même sans avoir
                    conduit!
                </strong>
            </p>
            <p>
                Dans quelles circonstances cela peut-il arriver ? Lorsqu’une
                personne a les facultés affaiblies par l’alcool et elle a la
                garde ou le contrôle d’un véhicule, elle peut faire l’objet
                d’accusations, même si le véhicule est immobile. Heureusement,
                il est possible de contester ce type d’accusation!
            </p>
            <p>
                <Img
                    fluid={data.img0.childImageSharp.fluid}
                    title="Garde ou contrôle du véhicule et alcool au volant!"
                    alt=""
                />
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>
                                Dans cet article, nous vous expliquons en quoi
                                la garde ou le contrôle du véhicule peuvent
                                entraîner une accusation de conduite avec
                                facultés affaiblies, et comment s’en défendre!
                            </strong>
                        </p>
                    </td>
                </tr>
            </table>
            <h2>
                La garde ou le contrôle d’un véhicule peut mener à une
                condamnation pour conduite avec facultés affaiblies, même sans
                avoir conduit!
            </h2>
            <p>
                Pour comprendre comment une personne peut être condamnée pour
                l’infraction de conduite avec les facultés affaiblies par
                l’alcool en vertu de{' '}
                <Link to="https://laws-lois.justice.gc.ca/fra/lois/c-46/section-320.14.html">
                    l’article 320.14
                </Link>{' '}
                du Code criminel, sans avoir conduit un véhicule, il est
                important de connaître les éléments essentiels de cette
                infraction.
            </p>
            <p>
                Pour qu’un individu soit reconnu coupable d’alcool au volant au
                Québec, la poursuite doit établir hors de tout doute raisonnable
                les trois éléments suivants :
            </p>
            <ul>
                <li>L’identité de la personne accusée</li>
                <li>La garde ou le contrôle du véhicule</li>
                <li>L’altération des capacités de conduire</li>
            </ul>
            <h3>L’identité de la personne accusée</h3>
            <p>
                Le premier élément que la poursuite doit établir est l’identité
                de la personne accusée!
            </p>
            <p>
                En effet, la poursuite, par l’entremise du procureur de la
                Couronne, doit démontrer que l’accusé était bien celui qui avait
                la garde ou le contrôle du véhicule au moment des faits.{' '}
            </p>
            <p>
                <strong>
                    La simple présence d’une personne en état d’ébriété à
                    proximité d’un véhicule ne suffit pas : l’accusé doit
                    clairement avoir conduit le véhicule ou en avoir eu la garde
                    ou le contrôle.
                </strong>
            </p>
            <p>
                Cette preuve peut être présentée de différentes façons. Un
                témoin peut affirmer avoir vu l’accusé conduire, ou un policier
                peut en avoir été témoin directement. D’autres éléments sont
                également recevables, comme des images provenant de caméras de
                surveillance ou une déclaration de l’accusé admettant avoir pris
                le volant.
            </p>
            <h3>La garde ou le contrôle du véhicule</h3>
            <p>
                Le deuxième critère devant obligatoirement être établi par la
                poursuite est que l’accusé avait la garde ou le contrôle du
                véhicule au moment des faits reprochés.
            </p>
            <p>
                C’est ici que la notion de « conduite » prend un sens élargi.
                Bien que l’article 320.14 du <em>Code criminel</em> mentionne le
                fait de conduire un véhicule à moteur,{' '}
                <strong>
                    il n’est techniquement pas nécessaire d’avoir déplacé le
                    véhicule pour être déclaré coupable
                </strong>
                . Être en position d’exercer un certain contrôle sur celui-ci
                peut suffire.
            </p>
            <p>
                Le <em>Code criminel</em> et la jurisprudence canadienne
                reconnaissent que{' '}
                <strong>
                    le simple fait d’être assis derrière le volant avec les clés
                    en sa possession constitue une présomption de conduite
                </strong>
                .
            </p>
            <p>
                {' '}
                C’est d’ailleurs ce que prévoit{' '}
                <Link to="https://www.canlii.org/fr/ca/legis/lois/lrc-1985-c-c-46/derniere/lrc-1985-c-c-46.html#art320.35">
                    l’article 320.35
                </Link>{' '}
                <em>Code criminel</em>:
            </p>
            <p>
                « Dans les poursuites engagées pour une infraction prévue aux
                articles 320.14 ou 320.15,{' '}
                <strong>
                    lorsqu’il est prouvé que l’accusé occupait la place ou la
                    position ordinairement occupée par la personne qui conduit
                    un moyen de transport
                </strong>
                , il est présumé l’avoir conduit à moins qu’il n’établisse qu’il
                n’occupait pas cette place ou cette position dans le but de
                mettre en mouvement le moyen de transport. »
            </p>
            <p>
                <Img
                    fluid={data.img1.childImageSharp.fluid}
                    title="Présomption de conduite du véhicule et alcool au volant!"
                    alt=""
                />
            </p>
            <h4>
                Lorsque le véhicule est immobile, est-il nécessaire de prouver
                que le conducteur avait l’intention de le conduire ?
            </h4>
            <p>
                NON, la preuve de l’intention de conduire n’est pas un élément
                essentiel de l’infraction liée à la garde ou au contrôle d’un
                véhicule à moteur. La Couronne n’a pas à démontrer que l’accusé
                avait l’intention de prendre le volant.
            </p>
            <h4>
                Pourquoi la loi prévoit-elle cette présomption de conduite ?
            </h4>
            <p>
                La raison d’être de cette présomption est de{' '}
                <strong>
                    protéger le public contre le risque que représente une
                    personne en état d’ébriété susceptible de conduire
                </strong>
                . Si une personne a la garde ou le contrôle d’un véhicule tout
                en étant intoxiquée, la loi présume qu’elle pourrait, à tout
                moment, mettre le véhicule en marche, même si elle n’en a pas
                l’intention immédiate.{' '}
            </p>
            <ul>
                <li>
                    <strong>
                        Cette présomption légale vise à prévenir les accidents
                        avant qu’ils ne surviennent
                    </strong>
                    . En effet, le législateur a ainsi voulu{' '}
                    <strong>favoriser la sécurité routière</strong> en
                    élargissant la portée de l’infraction de conduite avec les
                    facultés affaiblies.
                </li>
            </ul>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>
                                Exemple de garde ou de contrôle d’un véhicule
                                sans conduite :
                            </strong>
                        </p>
                        <p>
                            Une personne qui, après avoir consommé de l’alcool,
                            choisit de dormir dans son véhicule dans l’intention
                            d’éviter de conduire peut tout de même faire face à
                            des accusations. Si elle est retrouvée assise sur le
                            siège du conducteur, avec les clés dans ses poches
                            ou à proximité, elle pourrait être considérée comme
                            ayant la garde ou le contrôle du véhicule.
                        </p>
                        <p>
                            Même si le moteur est éteint et que la personne
                            affirme ne pas avoir eu l’intention de conduire, la
                            loi prévoit une présomption de garde ou de contrôle
                            dans de telles circonstances. Cette présomption peut
                            suffire à entraîner une déclaration de culpabilité
                            pour conduite avec les facultés affaiblies par
                            l’alcool.
                        </p>
                    </td>
                </tr>
            </table>
            <p>
                Toutefois, cette présomption peut être renversée si l’accusé
                démontre qu’il n’occupait pas cette position dans le but de
                mettre le véhicule en mouvement!
            </p>
            <h3>L’altération des capacités</h3>
            <p>
                Le troisième élément nécessaire pour établir l’infraction de
                conduite avec les facultés affaiblies par l’alcool est la preuve
                que les capacités de la personne accusée étaient altérées. Cela
                signifie que sa capacité à conduire en toute sécurité était
                affaiblie par l’alcool, les drogues ou une combinaison des deux.
            </p>
            <p>
                Selon l’article 320.14 du <em>Code criminel</em>, deux
                situations peuvent entraîner une condamnation :
            </p>
            <ul>
                <li>
                    La conduite avec les facultés affaiblies à un quelconque
                    degré par l’effet de l’alcool, d’une drogue, ou par l’effet
                    combiné de l’alcool et d’une drogue ;
                </li>
                <li>
                    La conduite avec un taux d’alcoolémie égal ou supérieur à 80
                    mg/100 ml, mesuré dans les deux heures suivant le moment où
                    la personne a cessé de conduire ou d’avoir le contrôle du
                    véhicule.
                </li>
            </ul>
            <p>
                Toutefois, une exception existe à cette deuxième situation : si
                la personne a consommé de l’alcool uniquement après avoir cessé
                de conduire, et que cette consommation explique entièrement le
                taux d’alcool mesuré.
            </p>
            <p>
                <Img
                    fluid={data.img2.childImageSharp.fluid}
                    title="Prouver un taux d’alcoolémie illégal chez le conducteur!"
                    alt=""
                />
            </p>
            <p>
                Pour prouver l’affaiblissement des facultés, la poursuite peut
                s’appuyer sur divers éléments, notamment les résultats d’un
                alcootest, les observations des policiers (parole confuse,
                démarche instable, odeur d’alcool, etc.), des témoignages de
                tiers ou les résultats d’analyses toxicologiques.
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            Il est important de noter qu’être reconnu coupable
                            de conduite avec les facultés affaiblies par
                            l’alcool entraîne de{' '}
                            <Link to="https://avocatsalcoolauvolant.ca/consequences-alcool-volant/">
                                <strong>
                                    nombreuses conséquences au Québec
                                </strong>
                            </Link>
                            . Parmi celles-ci, on retrouve:
                        </p>
                        <ul>
                            <li>Une amende minimale de 1000 $</li>
                            <li>L’ouverture d’un casier judiciaire</li>
                            <li>La révocation du permis de conduire</li>
                            <li>La possibilité d’une peine d’emprisonnement</li>
                        </ul>
                    </td>
                </tr>
            </table>
            <p>
                <strong>
                    Pour éviter une condamnation, il est fortement recommandé de
                    faire appel à un avocat spécialisé en{' '}
                </strong>
                <Link to="https://avocatsalcoolauvolant.ca/avocat-criminel-alcool-volant/">
                    <strong>droit criminel</strong>
                </Link>
                <strong>!</strong>
            </p>
            <h2>
                <strong>
                    <strong>
                        Comment se défendre contre une accusation de conduite
                        avec facultés affaiblies basée sur la garde ou le
                        contrôle du véhicule?
                    </strong>
                </strong>
            </h2>
            <p>
                Comme mentionné plus haut, dans les affaires de conduite avec
                les facultés affaiblies par l’alcool, il est possible d’être
                accusé même si le véhicule est immobile, à condition que la
                personne ait eu la garde ou le contrôle du véhicule.
                Heureusement, cette présomption de conduite n’est pas absolue!{' '}
            </p>
            <ul>
                <li>
                    En effet, l’article 320.35 du <em>Code criminel</em> prévoit
                    qu’il est présumé avoir conduit le véhicule, sauf si
                    l’accusé établit{' '}
                    <strong>
                        qu’il n’occupait pas cette place ou cette position dans
                        le but de mettre le véhicule en mouvement!
                    </strong>
                </li>
            </ul>
            <h3>
                <strong>
                    <strong>
                        Contester l’intention de mettre le véhicule en marche
                    </strong>
                </strong>
            </h3>
            <p>
                L’un des principaux arguments de la défense consiste à démontrer
                que l’accusé n’avait aucune intention réelle de conduire le
                véhicule. Par exemple, une personne peut prouver qu’elle a
                volontairement choisi de ne pas prendre le volant, comme dans le
                cas où elle décide de dormir à l’arrière du véhicule et place
                les clés hors de portée.{' '}
            </p>
            <p>
                Le tribunal peut être convaincu par une preuve crédible,
                notamment par des témoignages ou des circonstances démontrant
                qu’il n’y avait pas de volonté de conduire.
            </p>
            <h4>
                Quel est le fardeau de la preuve pour l’accusé voulant renverser
                la présomption de conduite?
            </h4>
            <p>
                Il est important de noter que l’accusé n’a pas à prouver hors de
                tout doute raisonnable qu’il n’avait pas l’intention de
                conduire. Le fardeau de preuve repose sur la{' '}
                <strong>prépondérance des probabilités</strong>, c’est-à-dire
                qu’il doit être plus probable que non (plus de 50 %) que
                l’accusé n’avait pas l’intention de conduire.{' '}
            </p>
            <p>
                En d’autres termes, les arguments présentés pour soutenir
                l’absence d’intention de conduire doivent être jugés plus
                plausibles que leur contraire.
            </p>
            <p>
                <Img
                    fluid={data.img3.childImageSharp.fluid}
                    title="Défense pour renverser la présomption de conduite!"
                    alt=""
                />
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>
                                Exemple concret : R. c. Charrette (2023)
                            </strong>
                        </p>
                        <p>
                            Dans cette affaire récente, Mme Charrette était
                            accusée de conduite avec les facultés affaiblies par
                            l’alcool, présumée en contrôle du véhicule parce
                            qu’elle était assise au volant. Elle a contesté
                            cette présomption en expliquant qu’elle ne voulait
                            pas conduire.{' '}
                        </p>
                        <p>
                            En effet, elle s’était assise brièvement pour
                            appeler un ami, M. Gagnon, afin qu’il vienne la
                            chercher, car elle avait trop bu. Elle a aussi
                            justifié d’avoir mis le moteur en marche seulement
                            pour activer le Bluetooth et se protéger du froid.{' '}
                        </p>
                        <p>
                            Le tribunal a trouvé son témoignage crédible et a
                            jugé qu’elle avait prouvé, selon la prépondérance
                            des probabilités, qu’elle ne voulait pas conduire.
                            La présomption de contrôle du véhicule ne s’est donc
                            pas appliquée, et Mme Charrette a été acquittée.
                        </p>
                    </td>
                </tr>
            </table>
            <h3>
                <strong>
                    <strong>
                        Remettre en cause la garde ou le contrôle du véhicule
                    </strong>
                </strong>
            </h3>
            <p>
                Remettre en cause la garde ou le contrôle du véhicule est une
                autre défense possible. La défense peut arguer que{' '}
                <strong>
                    l’accusé n’avait pas réellement la garde ou le contrôle du
                    véhicule parce que celui-ci était hors d’état de se déplacer
                    ou ne présentait aucun danger.{' '}
                </strong>
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            Par exemple, le véhicule peut être en panne, sans
                            essence, ou stationné de manière à rendre tout
                            mouvement impossible. De plus, si les clés du
                            véhicule sont en possession d’une autre personne
                            située ailleurs, inaccessible pour l’accusé, cela
                            démontre qu’il n’avait pas le contrôle effectif.
                        </p>
                    </td>
                </tr>
            </table>
            <ul>
                <li>
                    Cette défense vise à montrer que, malgré la présence de
                    l’accusé dans le véhicule, il n’y avait ni garde réelle ni
                    possibilité de conduite, ce qui permet de repousser la
                    présomption de contrôle ou de garde et d’éviter une
                    condamnation.
                </li>
            </ul>
            <p>
                Bref, même si la loi établit une présomption de contrôle
                lorsqu’une personne est assise au volant, cette présomption peut
                être contestée par une défense solide. En effet, l’intention
                réelle de conduire et la situation du véhicule sont des éléments
                que la défense peut utiliser pour renverser cette présomption.
            </p>
            <h2>
                Vous êtes accusé pour conduite avec les facultés affaiblies ?
                Consultez un avocat spécialisé en droit criminel !
            </h2>
            <p>
                Si vous avez été arrêté pour conduite avec les facultés
                affaiblies par l’alcool et que des accusations criminelles ont
                été portées contre vous parce que vous aviez la garde ou le
                contrôle du véhicule sans avoir conduit, il est fortement
                recommandé de faire appel à un avocat spécialisé en droit
                criminel.{' '}
            </p>
            <p>
                Une condamnation pour cette infraction peut entraîner des
                conséquences financières importantes et des répercussions graves
                sur votre vie professionnelle et personnelle. L’accompagnement
                d’un avocat spécialisé vous permet de bénéficier d’une défense
                adaptée et d’optimiser vos chances d’obtenir un acquittement en
                démontrant clairement que vous n’aviez pas l’intention de
                conduire.
            </p>
            <p>
                {' '}
                En effet, un avocat expérimenté en droit criminel préparera
                minutieusement votre dossier afin de présenter une défense
                solide et maximiser vos chances{' '}
                <Link to="https://avocatsalcoolauvolant.ca/acquittement-alcool-volant-erreur-police/">
                    <strong>d’être acquitté</strong>
                </Link>
                .
            </p>
            <p>
                <Img
                    fluid={data.img4.childImageSharp.fluid}
                    title="Avocat pour garde ou contrôle et alcool au volant !"
                    alt=""
                />
            </p>
            <p>
                <strong>
                    Trouvez rapidement un avocat en droit criminel grâce à notre
                    formulaire simple et gratuit ! Une fois votre demande
                    soumise, nous vous mettons en relation avec un avocat près
                    de chez vous.
                </strong>
            </p>
            <p>
                <strong>
                    Ce service est entièrement gratuit et sans engagement, vous
                    offrant la liberté de faire un choix en toute tranquillité!
                </strong>
            </p>
        </SeoPage>
    )
}

export default ContentPage

export const pageQuery = graphql`
    query ($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
        img0: file(
            relativePath: {
                eq: "accusation-controle-vehicule-alcool-volant/garde-controle-vehicule-alcool-volant.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img1: file(
            relativePath: {
                eq: "accusation-controle-vehicule-alcool-volant/presomption-conduite-vehicule-alcool-volant.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img2: file(
            relativePath: {
                eq: "accusation-controle-vehicule-alcool-volant/prouver-taux-alcoolemie-illegal-conducteur.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img3: file(
            relativePath: {
                eq: "accusation-controle-vehicule-alcool-volant/defense-presomption-conduite-alcool-volant.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img4: file(
            relativePath: {
                eq: "accusation-controle-vehicule-alcool-volant/avocat-controle-vehicule-alcool-volant.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
    }
`
